import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import {
  ArrowLeft,
  Scale,
  AlertTriangle,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  User,
  Building2,
  MessageSquare,
  Shield,
  Ban,
  CreditCard,
  FileWarning,
  Handshake,
  WifiOff,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "@/lib/demo-data";
import { toast } from "sonner";

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline"; icon: typeof Clock }
> = {
  REPORTED: { label: "Signale", variant: "outline", icon: AlertTriangle },
  OPEN: { label: "Ouvert", variant: "default", icon: Scale },
  IN_MEDIATION: { label: "En mediation", variant: "secondary", icon: Clock },
  RESOLVED: { label: "Resolu", variant: "default", icon: CheckCircle2 },
  CLOSED: { label: "Clos", variant: "outline", icon: XCircle },
  ESCALATED: { label: "Escalade", variant: "destructive", icon: AlertTriangle },
};

const REASON_LABELS: Record<string, string> = {
  POOR_QUALITY: "Mauvaise qualite des travaux",
  DELAY: "Retard important",
  NO_SHOW: "Artisan absent",
  PRICE_DISPUTE: "Litige sur le prix",
  DAMAGE: "Degats causes",
  NON_PAYMENT: "Non-paiement",
  INCOMPLETE_WORK: "Travaux incomplets",
  OTHER: "Autre",
};

const RESOLUTION_OPTIONS = [
  { value: "CREDIT_REFUND", label: "Remboursement credits", icon: CreditCard },
  { value: "WARNING", label: "Avertissement au pro", icon: FileWarning },
  { value: "SUSPENSION", label: "Suspension du pro", icon: Ban },
  { value: "MUTUAL_AGREEMENT", label: "Accord a l'amiable", icon: Handshake },
  { value: "NO_ACTION", label: "Sans suite", icon: XCircle },
];

export default function DisputeDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isWriter = canWrite(user?.staffRole || null);

  const [messageText, setMessageText] = useState("");
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [resolution, setResolution] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [escalateNote, setEscalateNote] = useState("");

  const { data: dispute, isLoading, isError, refetch } = trpc.admin.disputes.getById.useQuery(
    { id: id! } as any,
    { enabled: !!id, retry: false }
  );

  const { data: messagesData, refetch: refetchMessages } =
    trpc.admin.disputes.getMessages.useQuery({ disputeId: id! } as any, {
      enabled: !!id,
      retry: false,
    });

  const sendMessageMutation = trpc.admin.disputes.sendMessage.useMutation({
    onSuccess: () => {
      setMessageText("");
      refetchMessages();
      refetch();
      toast.success("Message envoye");
    },
    onError: () => toast.error("Erreur lors de l'envoi"),
  } as any);

  const updateStatusMutation = trpc.admin.disputes.updateStatus.useMutation({
    onSuccess: () => {
      refetch();
      toast.success("Statut mis a jour");
    },
    onError: () => toast.error("Erreur lors de la mise a jour"),
  } as any);

  const resolveMutation = trpc.admin.disputes.resolve.useMutation({
    onSuccess: () => {
      setShowResolveDialog(false);
      setResolution("");
      setResolutionNote("");
      refetch();
      toast.success("Litige resolu");
    },
    onError: () => toast.error("Erreur lors de la resolution"),
  } as any);

  const d = dispute as any;
  const messages = Array.isArray(messagesData) ? messagesData : (messagesData as any)?.messages ?? [];
  const timeline = d?.Timeline ?? [];

  const handleSendMessage = () => {
    if (!messageText.trim() || !id) return;
    (sendMessageMutation as any).mutateAsync({
      disputeId: id,
      content: messageText.trim(),
      senderType: "OPS",
    });
  };

  const handleOpenMediation = () => {
    if (!id) return;
    (updateStatusMutation as any).mutateAsync({
      id,
      status: "IN_MEDIATION",
    });
  };

  const handleEscalate = () => {
    if (!id || !escalateNote.trim()) return;
    (updateStatusMutation as any).mutateAsync({
      id,
      status: "ESCALATED",
      note: escalateNote.trim(),
    });
    setShowEscalateDialog(false);
    setEscalateNote("");
  };

  const handleResolve = () => {
    if (!id || !resolution) return;
    (resolveMutation as any).mutateAsync({
      id,
      resolution,
      note: resolutionNote.trim() || undefined,
    });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (isError || !d) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate("/disputes")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour aux litiges
          </Button>
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <WifiOff className="h-10 w-10 text-muted-foreground mb-4" />
              <p className="text-sm text-muted-foreground mb-4">
                L'endpoint backend pour les litiges n'est pas encore disponible.
              </p>
              <Button variant="outline" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Reessayer
              </Button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const status = STATUS_CONFIG[d.status] ?? STATUS_CONFIG.REPORTED;
  const StatusIcon = status.icon;
  const isOpen = ["REPORTED", "OPEN", "IN_MEDIATION", "ESCALATED"].includes(d.status);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/disputes")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold tracking-tight">
                  Litige {d.reference ?? `#${d.id?.slice(0, 8)}`}
                </h1>
                <Badge variant={status.variant} className="gap-1">
                  <StatusIcon className="h-3 w-3" />
                  {status.label}
                </Badge>
              </div>
              <p className="text-muted-foreground">
                {REASON_LABELS[d.reason] ?? d.reason} — Ouvert le{" "}
                {d.createdAt ? formatDateTime(d.createdAt) : "-"}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          {isWriter && isOpen && (
            <div className="flex items-center gap-2">
              {d.status === "REPORTED" && (
                <Button onClick={handleOpenMediation}>
                  <Scale className="h-4 w-4 mr-2" />
                  Ouvrir mediation
                </Button>
              )}
              {d.status !== "ESCALATED" && (
                <Button variant="destructive" onClick={() => setShowEscalateDialog(true)}>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Escalader
                </Button>
              )}
              <Button
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={() => setShowResolveDialog(true)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Resoudre
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left column — Details + Messages */}
          <div className="lg:col-span-2 space-y-6">
            {/* Parties */}
            <div className="grid gap-4 sm:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Particulier
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold">
                    {d.particulier
                      ? [d.particulier.firstName, d.particulier.lastName].filter(Boolean).join(" ") || "Non renseigne"
                      : "Non renseigne"}
                  </p>
                  <p className="text-sm text-muted-foreground">{d.particulier?.email ?? "-"}</p>
                  <p className="text-sm text-muted-foreground">{d.particulier?.phone ?? "-"}</p>
                  {d.Project && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Projet: {d.Project.title ?? d.Project.id}
                    </p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Professionnel
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold">{d.Company?.name ?? "Non renseigne"}</p>
                  <p className="text-sm text-muted-foreground">{d.Company?.email ?? "-"}</p>
                  <p className="text-sm text-muted-foreground">{d.Company?.phone ?? "-"}</p>
                </CardContent>
              </Card>
            </div>

            {/* Description */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Description du litige</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">
                  {d.description ?? "Aucune description fournie."}
                </p>
              </CardContent>
            </Card>

            {/* Resolution (if resolved) */}
            {d.resolution && (
              <Card className="border-green-200 bg-green-50/50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    Resolution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Badge variant="secondary" className="mb-2">
                    {RESOLUTION_OPTIONS.find((r) => r.value === d.resolution)?.label ??
                      d.resolution}
                  </Badge>
                  {d.resolutionNote && (
                    <p className="text-sm text-muted-foreground mt-2">{d.resolutionNote}</p>
                  )}
                  {d.resolvedAt && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Resolu le {formatDateTime(d.resolvedAt)} par {d.resolvedBy ?? "OPS"}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Messages / Exchange */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Echanges ({messages.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-6">
                    Aucun echange pour le moment
                  </p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {messages.map((msg: any) => {
                      const isOps = msg.senderType === "OPS";
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOps ? "justify-end" : "justify-start"}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg px-4 py-2 ${
                              isOps
                                ? "bg-primary text-primary-foreground"
                                : msg.senderType === "PARTICULIER"
                                ? "bg-blue-100 text-blue-900"
                                : "bg-orange-100 text-orange-900"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              {isOps ? (
                                <Shield className="h-3 w-3" />
                              ) : msg.senderType === "PARTICULIER" ? (
                                <User className="h-3 w-3" />
                              ) : (
                                <Building2 className="h-3 w-3" />
                              )}
                              <span className="text-xs font-medium">
                                {isOps
                                  ? "OPS"
                                  : msg.senderType === "PARTICULIER"
                                  ? "Particulier"
                                  : "Pro"}
                              </span>
                              <span className="text-xs opacity-70">
                                {msg.createdAt ? formatDateTime(msg.createdAt) : ""}
                              </span>
                            </div>
                            <p className="text-sm">{msg.content}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Send message */}
                {isWriter && isOpen && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Textarea
                      placeholder="Ecrire un message..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      rows={2}
                      className="flex-1"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                    />
                    <Button
                      size="icon"
                      className="self-end"
                      onClick={handleSendMessage}
                      disabled={(sendMessageMutation as any).isPending}
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column — Timeline */}
          <div className="space-y-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Chronologie</CardTitle>
              </CardHeader>
              <CardContent>
                {timeline.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-6">
                    <Clock className="h-5 w-5 mx-auto mb-2 opacity-50" />
                    L'historique apparaitra ici
                  </div>
                ) : (
                  <div className="space-y-4">
                    {timeline.map((event: any, idx: number) => (
                      <div key={idx} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <div className="h-2 w-2 rounded-full bg-primary mt-2" />
                          {idx < timeline.length - 1 && (
                            <div className="w-px flex-1 bg-border" />
                          )}
                        </div>
                        <div className="pb-4">
                          <p className="text-sm font-medium">{event.action}</p>
                          {event.note && (
                            <p className="text-xs text-muted-foreground">{event.note}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {event.createdAt ? formatDateTime(event.createdAt) : ""}
                            {event.by ? ` — ${event.by}` : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">Informations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Priorite</span>
                  <Badge
                    variant={
                      d.priority === "URGENT"
                        ? "destructive"
                        : d.priority === "HIGH"
                        ? "default"
                        : "outline"
                    }
                  >
                    {d.priority === "URGENT"
                      ? "Urgent"
                      : d.priority === "HIGH"
                      ? "Haute"
                      : d.priority === "MEDIUM"
                      ? "Moyenne"
                      : "Basse"}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Assigne a</span>
                  <span>{d.assignedTo ?? "Non assigne"}</span>
                </div>
                {d.estimatedCredits !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Credits en jeu</span>
                    <span className="font-medium">{d.estimatedCredits}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Resolve Dialog */}
      <Dialog open={showResolveDialog} onOpenChange={setShowResolveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resoudre le litige</DialogTitle>
            <DialogDescription>
              Choisissez une resolution et ajoutez une note explicative.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Resolution</label>
              <Select value={resolution} onValueChange={setResolution}>
                <SelectTrigger>
                  <SelectValue placeholder="Choisir une resolution" />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    return (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Note (optionnel)</label>
              <Textarea
                placeholder="Details de la resolution..."
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolveDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleResolve}
              disabled={!resolution || (resolveMutation as any).isPending}
              className="bg-green-600 hover:bg-green-700"
            >
              Confirmer la resolution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalader le litige</DialogTitle>
            <DialogDescription>
              Escalader ce litige au niveau superieur. Ajoutez une justification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Textarea
              placeholder="Raison de l'escalade..."
              value={escalateNote}
              onChange={(e) => setEscalateNote(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleEscalate}
              disabled={!escalateNote.trim() || (updateStatusMutation as any).isPending}
            >
              Confirmer l'escalade
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
